import React, { useState, useEffect } from 'react';
import * as Bixby from '$/core';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import NoDataFound from '$/components/global/noData';
import './CloSummary.scss';
import AnalyticsHeaderFilter from '$/components/analyticsHeaderFilter';
import { ClientAnalyticsService } from '$/services';
import dayjs from 'dayjs';
import { colorCode, leverageColorCode } from '$/pages/client/clo-analytics/heatMap';

interface ICloSummaryProps {
  dataForGrid: Bixby.Models.CloAnalytics[];
  heatMapActive: boolean;
}

const CloSummary: React.FC<ICloSummaryProps> = props => {
  const { dataForGrid, heatMapActive } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [topQuartileData, setTopQuartileData] = useState(undefined as Bixby.Models.CompanyAnalytics | undefined);
  const [medianData, setMedianData] = useState(undefined as Bixby.Models.CompanyAnalytics | undefined);
  const [bottomQuartileData, setBottomQuartileData] = useState(undefined as Bixby.Models.CompanyAnalytics | undefined);

  const analyticsMetrics: Bixby.Enums.AnalyticsMetrics[] = Object.values(Bixby.Enums.AnalyticsMetrics);

  useEffect(() => {
    async function getMarketAnalytics() {
      const result: Bixby.Models.CompanyAnalyticsResult = await ClientAnalyticsService.getMarketDataAnalytics();
      const top = result?.allCompanies.find(x => x.name[1] === 'Top Quartile');
      const median = result?.allCompanies.find(x => x.name[1] === 'Median');
      const bottom = result?.allCompanies.find(x => x.name[1] === 'Bottom Quartile');
      setTopQuartileData(top);
      setMedianData(median);
      setBottomQuartileData(bottom);
    }
    getMarketAnalytics();
  }, []);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const valueFormatter = (params: any, formatter: (value: any) => string, fallback: string) => {
    if (params.value !== null && params.value !== undefined) {
      return formatter(params.value);
    } else if (!params.node.allChildrenCount) {
      return fallback;
    }
    return '';
  };

  class AnalyticsIdentifierCellRenderer {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      let value = params.value;
      const isArray = Bixby.Utils.isArray(params.value);
      if (isArray) {
        value = params.value[params.value.length - 1];

        newDiv.innerHTML = '<span>' + value + '</span>';
      } else if (params.node.allLeafChildren.length > 0) {
        const id = params.node.allLeafChildren[0].data['id'];
        newDiv.innerHTML = '<a class="link_underline" href="/clo-analytics/' + id + '">' + value + '</a>';
      } else {
        newDiv.innerHTML = '<span>' + value + '</span>';
      }

      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class ManagerIdentifierCellRenderer {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let manager = '';
      if (leafs.length > 1) {
        manager = leafs[0].data['manager'];
      }
      newDiv.innerHTML = '<span>' + manager + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class VintageIdentifierCellRenderer {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let vintage = '';
      if (leafs.length > 1) {
        vintage = leafs[0].data['vintage'];
      }
      newDiv.innerHTML = '<span class="analytics-name">' + vintage + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class AsOfDateIdentifierCellRenderer {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let asOfDate = '';
      if (leafs.length > 1) {
        asOfDate = leafs[0].data['asOfDate'];
      }
      newDiv.innerHTML = '<span class="analytics-name">' + asOfDate + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class ReinvestmentDateIdentifierCellRender {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let reinvestmentDate = '';
      if (leafs.length > 1) {
        reinvestmentDate = leafs[0].data['reinvestmentDate'];
        reinvestmentDate = formatDate(reinvestmentDate);
      }
      newDiv.innerHTML = '<span class="analytics-name">' + reinvestmentDate + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class PercentLess1TimeEbitdaInterestPortfolioCellRender {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let percentLess1TimeEbitdaInterestPortfolio = '';
      if (leafs.length > 1) {
        const percentLess1TimeInput = leafs[0].data['percentLess1TimeEbitdaInterestPortfolio'];
        if (percentLess1TimeInput != undefined)
          percentLess1TimeEbitdaInterestPortfolio = formatPercentage(percentLess1TimeInput);
        else percentLess1TimeEbitdaInterestPortfolio = 'NM';
      }
      newDiv.innerHTML = '<span class="analytics-name">' + percentLess1TimeEbitdaInterestPortfolio + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class PercentLess1TimeAdjustedEbitdaInterestPortfolioCellRender {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let percentLess1TimeAdjustedEbitdaInterestPortfolio = '';
      if (leafs.length > 1) {
        const percentLess1TimeAdjustedInput = leafs[0].data['percentLess1TimeAdjustedEbitdaInterestPortfolio'];
        if (percentLess1TimeAdjustedInput != undefined)
          percentLess1TimeAdjustedEbitdaInterestPortfolio = formatPercentage(percentLess1TimeAdjustedInput);
        else percentLess1TimeAdjustedEbitdaInterestPortfolio = 'NM';
      }
      newDiv.innerHTML = '<span class="analytics-name">' + percentLess1TimeAdjustedEbitdaInterestPortfolio + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class PercentGreater10TimesEbitdaLeveragePortfolioCellRender {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let percentGreater10TimesEbitdaLeveragePortfolio = '';
      if (leafs.length > 1) {
        const percentGreater10TimesInput = leafs[0].data['percentGreater10TimesEbitdaLeveragePortfolio'];
        if (percentGreater10TimesInput != undefined)
          percentGreater10TimesEbitdaLeveragePortfolio = formatPercentage(percentGreater10TimesInput);
        else percentGreater10TimesEbitdaLeveragePortfolio = 'NM';
      }
      newDiv.innerHTML = '<span class="analytics-name">' + percentGreater10TimesEbitdaLeveragePortfolio + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  class PercentGreater10TimesAdjustedEbitdaLeveragePortfolioCellRender {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      const leafs = params.node.allLeafChildren;
      let percentGreater10TimesAdjustedEbitdaLeveragePortfolio = '';
      if (leafs.length > 1) {
        const percentGreater10TimesAdjustedInput =
          leafs[0].data['percentGreater10TimesAdjustedEbitdaLeveragePortfolio'];
        if (percentGreater10TimesAdjustedInput != undefined)
          percentGreater10TimesAdjustedEbitdaLeveragePortfolio = formatPercentage(percentGreater10TimesAdjustedInput);
        else percentGreater10TimesAdjustedEbitdaLeveragePortfolio = 'NM';
      }
      newDiv.innerHTML =
        '<span class="analytics-name">' + percentGreater10TimesAdjustedEbitdaLeveragePortfolio + '</span>';
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  const formatCurrency = (value: number, fallback: string) => {
    const parsedValue: number = parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return fallback;
    }

    // add in appropriate formatting
    let prefix = '$';
    let suffix = '';
    if (parsedValue < 0) {
      prefix = prefix + '(';
      suffix = suffix + ')';
    }

    // remove hyphen
    const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

    const currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,').split('.')[0];

    return prefix + currency + suffix;
  };

  const formatMultiplier = (value: number) => {
    const percentage = value.toFixed(1);
    return `${percentage}x`;
  };

  const formatPercentage = (value: number, fractionDigits?: number | undefined) => {
    const percentage = (value * 100).toFixed(fractionDigits || 0);
    return `${percentage}%`;
  };

  const formatDate = (value: string) => {
    if (value) {
      return dayjs(value).format('MM/DD/YY');
    }
    return '';
  };

  const getCellClass = (
    params: any,
    heatMapActive: boolean,
    useLeverageColorCode: boolean,
    topQuartileDataValue?: number | null,
    medianQuartileDataValue?: number | null,
    bottomQuartileDataValue?: number | null
  ) => {
    if (
      topQuartileDataValue &&
      medianQuartileDataValue &&
      bottomQuartileDataValue &&
      params.node.key !== Bixby.Enums.AnalyticsMetrics.BottomDecile &&
      params.node.key !== Bixby.Enums.AnalyticsMetrics.TopDecile
    ) {
      if (useLeverageColorCode) {
        return leverageColorCode(
          heatMapActive,
          params,
          topQuartileDataValue,
          medianQuartileDataValue,
          bottomQuartileDataValue
        );
      } else {
        return colorCode(heatMapActive, params, topQuartileDataValue, medianQuartileDataValue, bottomQuartileDataValue);
      }
    } else return '';
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const getRowHeight = (params: any) => {
    if (analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)) {
      return 20;
    }
    return 32;
  };

  return (
    <React.Fragment>
      <section className="analytics-section">
        {dataForGrid && !Bixby.Utils.isNullOrEmpty(dataForGrid) ? (
          <div className="clo_summary_grid analytics-data-grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 150,
                    resizable: true,
                    lockPinned: true,
                    filter: 'agNumberColumnFilter',
                    cellClass: 'align-center',
                  }}
                  onGridReady={onGridReady}
                  rowData={dataForGrid}
                  suppressDragLeaveHidesColumns={true}
                  suppressClipboardPaste={true}
                  suppressMovableColumns={true}
                  getContextMenuItems={getContextMenuItems}
                  treeData={true}
                  animateRows={true}
                  groupDefaultExpanded={-1}
                  groupDisplayType="custom"
                  getDataPath={function (data) {
                    return data.name;
                  }}
                  rowClassRules={{
                    metric: params => {
                      return analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics);
                    },
                  }}
                  getRowHeight={getRowHeight}
                  headerHeight={32}
                  components={{
                    headerFilter: AnalyticsHeaderFilter,
                    AnalyticsIdentifierCellRenderer: AnalyticsIdentifierCellRenderer,
                  }}
                  columnDefs={[
                    {
                      headerGroupComponent: 'headerFilter',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'name',
                          cellRenderer: 'agGroupCellRenderer',
                          valueFormatter: params => params.node?.key || '',
                          cellClassRules: {
                            sector: params => {
                              return (
                                params.node.level === 0 &&
                                !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                              );
                            },
                            'industry-group': params => {
                              return (
                                params.node.level === 1 &&
                                !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                              );
                            },
                          },
                          headerClass: 'locked-header-note',
                          headerComponentParams: {
                            template:
                              '<div class="analytics-name">' +
                              '<span class="sector">CLO Name (total issuers, % coverage)</span>' +
                              '</div>',
                          },
                          minWidth: 350,
                          cellRendererParams: { suppressCount: true, innerRenderer: AnalyticsIdentifierCellRenderer },
                          pinned: 'left',
                          showRowGroup: true,
                        },
                      ],
                    },
                    {
                      headerName: 'CLO',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'manager',
                          filter: true,
                          valueFormatter: params => params.value || '',
                          cellRenderer: 'agGroupCellRenderer',
                          minWidth: 150,
                          cellRendererParams: { suppressCount: true, innerRenderer: ManagerIdentifierCellRenderer },
                          showRowGroup: 'manager',
                          cellClass: 'column-row-green',
                        },
                        {
                          field: 'vintage',
                          filter: true,
                          cellRenderer: 'agGroupCellRenderer',
                          valueFormatter: params => params.value || '',
                          minWidth: 105,
                          cellRendererParams: { suppressCount: true, innerRenderer: VintageIdentifierCellRenderer },
                          showRowGroup: 'vintage',
                          cellClass: 'column-row-green',
                        },
                        {
                          field: 'asOfDate',
                          headerName: 'As Of',
                          filter: true,
                          cellRenderer: 'agGroupCellRenderer',
                          minWidth: 105,
                          cellRendererParams: { suppressCount: true, innerRenderer: AsOfDateIdentifierCellRenderer },
                          showRowGroup: 'asOfDate',
                          cellClass: 'column-row-green',
                        },
                        {
                          field: 'reinvestmentDate',
                          headerName: 'Reinvest',
                          filter: 'agSetColumnFilter',
                          filterParams: {
                            values: ['In Reinvest', 'Outside Reinvest'],
                          },
                          filterValueGetter: function (params: any) {
                            if (params.data) {
                              if (!params.data.reinvestmentDate) {
                                return 'Outside Reinvest';
                              }

                              const now = dayjs(new Date());
                              const rowDate = dayjs(params.data.reinvestmentDate);

                              if (rowDate.isSameOrAfter(now, 'day')) {
                                return 'In Reinvest';
                              } else {
                                return 'Outside Reinvest';
                              }
                            }
                          },
                          cellRenderer: 'agGroupCellRenderer',
                          minWidth: 130,
                          cellRendererParams: {
                            suppressCount: true,
                            innerRenderer: ReinvestmentDateIdentifierCellRender,
                          },
                          showRowGroup: 'reinvestmentDate',
                          cellClass: 'column-row-green',
                        },
                      ],
                    },
                    {
                      headerName: '% of portfolio < 1x ICR',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'percentLess1TimeEbitdaInterestPortfolio',
                          headerName: 'EBITDA',
                          filter: 'agNumberColumnFilter',
                          cellRenderer: 'agGroupCellRenderer',
                          minWidth: 100,
                          cellRendererParams: {
                            suppressCount: true,
                            innerRenderer: PercentLess1TimeEbitdaInterestPortfolioCellRender,
                          },
                          showRowGroup: 'percentLess1TimeEbitdaInterestPortfolio',
                          cellClass: 'column-row-green',
                        },
                        {
                          field: 'percentLess1TimeAdjustedEbitdaInterestPortfolio',
                          headerName: 'Adj. EBITDA',
                          filter: 'agNumberColumnFilter',
                          cellRenderer: 'agGroupCellRenderer',
                          minWidth: 130,
                          cellRendererParams: {
                            suppressCount: true,
                            innerRenderer: PercentLess1TimeAdjustedEbitdaInterestPortfolioCellRender,
                          },
                          showRowGroup: 'percentLess1TimeAdjustedEbitdaInterestPortfolio',
                          cellClass: 'column-row-green',
                        },
                      ],
                    },
                    {
                      headerName: '% of portfolio > 10x 1L Lev',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'percentGreater10TimesEbitdaLeveragePortfolio',
                          headerName: 'EBITDA',
                          filter: 'agNumberColumnFilter',
                          cellRenderer: 'agGroupCellRenderer',
                          minWidth: 100,
                          cellRendererParams: {
                            suppressCount: true,
                            innerRenderer: PercentGreater10TimesEbitdaLeveragePortfolioCellRender,
                          },
                          showRowGroup: 'percentGreater10TimesEbitdaLeveragePortfolio',
                          cellClass: 'column-row-green',
                        },
                        {
                          field: 'percentGreater10TimesAdjustedEbitdaLeveragePortfolio',
                          headerName: 'Adj. EBITDA',
                          filter: 'agNumberColumnFilter',
                          cellRenderer: 'agGroupCellRenderer',
                          minWidth: 130,
                          cellRendererParams: {
                            suppressCount: true,
                            innerRenderer: PercentGreater10TimesAdjustedEbitdaLeveragePortfolioCellRender,
                          },
                          showRowGroup: 'percentGreater10TimesAdjustedEbitdaLeveragePortfolio',
                          cellClass: 'column-row-green',
                        },
                      ],
                    },
                    {
                      headerName: 'EBITDA Leverage',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'firstLienDebtGrossEbitda',
                          headerName: '1st Lien',
                          minWidth: 100,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              true,
                              topQuartileData?.firstLienDebtGrossEbitda,
                              medianData?.firstLienDebtGrossEbitda,
                              bottomQuartileData?.firstLienDebtGrossEbitda
                            ),
                        },
                        {
                          field: 'totalDebtGrossEbitda',
                          headerName: 'Total',
                          minWidth: 100,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              true,
                              topQuartileData?.totalDebtGrossEbitda,
                              medianData?.totalDebtGrossEbitda,
                              bottomQuartileData?.totalDebtGrossEbitda
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'Adj. EBITDA Leverage',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'firstLienDebtGrossAdjustedEbitda',
                          headerName: '1st Lien',
                          minWidth: 100,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              true,
                              topQuartileData?.firstLienDebtGrossAdjustedEbitda,
                              medianData?.firstLienDebtGrossAdjustedEbitda,
                              bottomQuartileData?.firstLienDebtGrossAdjustedEbitda
                            ),
                        },
                        {
                          field: 'totalDebtGrossAdjustedEbitda',
                          headerName: 'Total',
                          minWidth: 100,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              true,
                              topQuartileData?.totalDebtGrossAdjustedEbitda,
                              medianData?.totalDebtGrossAdjustedEbitda,
                              bottomQuartileData?.totalDebtGrossAdjustedEbitda
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'Interest Coverage',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'interestCoverageEbitda',
                          headerName: 'EBITDA',
                          minWidth: 130,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.interestCoverageEbitda,
                              medianData?.interestCoverageEbitda,
                              bottomQuartileData?.interestCoverageEbitda
                            ),
                        },
                        {
                          field: 'interestCoverageAdjustedEbitda',
                          headerName: 'Adj. EBITDA',
                          minWidth: 130,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.interestCoverageAdjustedEbitda,
                              medianData?.interestCoverageAdjustedEbitda,
                              bottomQuartileData?.interestCoverageAdjustedEbitda
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'Interest Coverage (After Capex)',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'interestCoverageAfterCapitalExpendituresEbitda',
                          headerName: 'EBITDA',
                          minWidth: 130,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.interestCoverageAfterCapitalExpendituresEbitda,
                              medianData?.interestCoverageAfterCapitalExpendituresEbitda,
                              bottomQuartileData?.interestCoverageAfterCapitalExpendituresEbitda
                            ),
                        },
                        {
                          field: 'interestCoverageAfterCapitalExpendituresAdjustedEbitda',
                          headerName: 'Adj. EBITDA',
                          minWidth: 130,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                              medianData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                              bottomQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda
                            ),
                        },
                      ],
                    },
                    {
                      field: 'liquidity',
                      headerName: 'Liquidity (1)',
                      minWidth: 130,
                      valueFormatter: params =>
                        valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                      cellClass: (params: any) =>
                        getCellClass(
                          params,
                          heatMapActive,
                          false,
                          topQuartileData?.liquidity,
                          medianData?.liquidity,
                          bottomQuartileData?.liquidity
                        ),
                    },
                    {
                      headerName: 'MRQ Growth %',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'revenueGrowth',
                          headerName: 'Revenue',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.revenueGrowth,
                              medianData?.revenueGrowth,
                              bottomQuartileData?.revenueGrowth
                            ),
                        },
                        {
                          field: 'ebitdaGrowth',
                          headerName: 'EBITDA',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.ebitdaGrowth,
                              medianData?.ebitdaGrowth,
                              bottomQuartileData?.ebitdaGrowth
                            ),
                        },
                        {
                          field: 'adjustedEbitdaGrowth',
                          headerName: 'Adj. EBITDA',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.adjustedEbitdaGrowth,
                              medianData?.adjustedEbitdaGrowth,
                              bottomQuartileData?.adjustedEbitdaGrowth
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'Margin %',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'ebitdaMargin',
                          headerName: 'EBITDA',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.ebitdaMargin,
                              medianData?.ebitdaMargin,
                              bottomQuartileData?.ebitdaMargin
                            ),
                        },
                        {
                          field: 'adjustedEbitdaMargin',
                          headerName: 'Adj. EBITDA',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.adjustedEbitdaMargin,
                              medianData?.adjustedEbitdaMargin,
                              bottomQuartileData?.adjustedEbitdaMargin
                            ),
                        },
                        {
                          field: 'ocfMargin',
                          headerName: 'OCF',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.ocfMargin,
                              medianData?.ocfMargin,
                              bottomQuartileData?.ocfMargin
                            ),
                        },
                        {
                          field: 'capitalExpendituresMargin',
                          headerName: 'OCF-Capex',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.capitalExpendituresMargin,
                              medianData?.capitalExpendituresMargin,
                              bottomQuartileData?.capitalExpendituresMargin
                            ),
                        },
                      ],
                    },
                    {
                      headerName: '1st Lien Loan Details',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'firstLien1Amount',
                          headerName: '$ Amt.',
                          minWidth: 120,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.firstLien1Amount,
                              medianData?.firstLien1Amount,
                              bottomQuartileData?.firstLien1Amount
                            ),
                        },
                        {
                          field: 'firstLien1Spread',
                          headerName: 'Spread',
                          minWidth: 120,
                          valueFormatter: params =>
                            valueFormatter(
                              params,
                              (value: any) => {
                                return value;
                              },
                              'NM'
                            ),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.firstLien1Spread,
                              medianData?.firstLien1Spread,
                              bottomQuartileData?.firstLien1Spread
                            ),
                        },
                      ],
                    },
                    {
                      field: 'totalEnterpriseValue',
                      headerName: 'TEV',
                      minWidth: 100,
                      valueFormatter: params =>
                        valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                      cellClass: (params: any) =>
                        getCellClass(
                          params,
                          heatMapActive,
                          false,
                          topQuartileData?.totalEnterpriseValue,
                          medianData?.totalEnterpriseValue,
                          bottomQuartileData?.totalEnterpriseValue
                        ),
                    },
                    {
                      headerName: 'Current Loan-to-Value (2)',
                      children: [
                        {
                          field: 'firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                          headerName: '1st Lien',
                          minWidth: 120,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                              medianData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                              bottomQuartileData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda
                            ),
                        },
                        {
                          field: 'totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                          headerName: 'Total',
                          minWidth: 120,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                              medianData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                              bottomQuartileData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'TEV /',
                      children: [
                        {
                          field: 'totalEnterpriseValueByRevenue',
                          headerName: 'Revenue',
                          minWidth: 130,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.totalEnterpriseValueByRevenue,
                              medianData?.totalEnterpriseValueByRevenue,
                              bottomQuartileData?.totalEnterpriseValueByRevenue
                            ),
                        },
                        {
                          field: 'totalEnterpriseValueByAdjustedManagementEbitda',
                          headerName: 'Adj. EBITDA',
                          minWidth: 130,
                          valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.totalEnterpriseValueByAdjustedManagementEbitda,
                              medianData?.totalEnterpriseValueByAdjustedManagementEbitda,
                              bottomQuartileData?.totalEnterpriseValueByAdjustedManagementEbitda
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'Financials at transaction',
                      children: [
                        {
                          field: 'revenueAtBuyout',
                          headerName: 'Revenue',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.revenueAtBuyout,
                              medianData?.revenueAtBuyout,
                              bottomQuartileData?.revenueAtBuyout
                            ),
                        },
                        {
                          field: 'managementAdjustedEbitdaAtBuyout',
                          headerName: 'Adj. EBITDA',
                          minWidth: 130,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.managementAdjustedEbitdaAtBuyout,
                              medianData?.managementAdjustedEbitdaAtBuyout,
                              bottomQuartileData?.managementAdjustedEbitdaAtBuyout
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'Capital Structure at transaction',
                      children: [
                        {
                          field: 'netDebtPercentageOfCap',
                          headerName: 'Net Debt % of Cap',
                          minWidth: 200,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.netDebtPercentageOfCap,
                              medianData?.netDebtPercentageOfCap,
                              bottomQuartileData?.netDebtPercentageOfCap
                            ),
                        },
                        {
                          field: 'totalEquityPercentageOfCap',
                          headerName: 'Total Equity % of Cap',
                          minWidth: 200,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.totalEquityPercentageOfCap,
                              medianData?.totalEquityPercentageOfCap,
                              bottomQuartileData?.totalEquityPercentageOfCap
                            ),
                        },
                      ],
                    },
                    {
                      headerName: 'Change since transaction',
                      children: [
                        {
                          field: 'cumulativeDividendsByImpliedEquityAtRisk',
                          headerName: 'Dividends / Equity "at risk" (3)',
                          minWidth: 250,
                          valueFormatter: params =>
                            valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          cellClass: (params: any) =>
                            getCellClass(
                              params,
                              heatMapActive,
                              false,
                              topQuartileData?.cumulativeDividendsByImpliedEquityAtRisk,
                              medianData?.cumulativeDividendsByImpliedEquityAtRisk,
                              bottomQuartileData?.cumulativeDividendsByImpliedEquityAtRisk
                            ),
                        },
                      ],
                    },
                  ]}></AgGridReact>
              </div>
            </div>
          </div>
        ) : (
          <div className="analytics-not-found-section">
            <NoDataFound dataType="analytics" />
          </div>
        )}
      </section>
    </React.Fragment>
  );
};
export default CloSummary;
